import { useRef, useState } from "react";
import { Lock, Upload, Loader2, X } from "lucide-react";

// Shown when a founder uploads a password-protected bank statement. Two equal
// options: unlock it server-side with their password (never stored), or pick
// an unlocked export instead.
export const EncryptedPdfDialog = ({ filename, busy, error, onSubmitPassword, onPickUnlocked, onClose }) => {
  const [password, setPassword] = useState("");
  const fileRef = useRef(null);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="encrypted-pdf-dialog">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-100">
              <Lock className="h-4 w-4 text-amber-600" />
            </span>
            <div>
              <p className="text-sm font-bold text-neutral-900">Password-protected bank statement</p>
              {filename && <p className="text-[11px] text-neutral-500">{filename}</p>}
            </div>
          </div>
          <button data-testid="enc-dialog-close" onClick={onClose} className="rounded-full p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700">
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="mt-3 text-sm leading-relaxed text-neutral-600" data-testid="enc-dialog-message">
          This bank statement appears to be password-protected. Most banks send
          statements with a password for security. You can either enter the
          password below so we can unlock it for you, or upload an unlocked
          version instead.
        </p>

        {error && (
          <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs font-semibold text-red-700" data-testid="enc-dialog-error">{error}</p>
        )}

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {/* Option 1 — enter password */}
          <div className="rounded-xl border border-neutral-200 p-4">
            <p className="text-xs font-bold uppercase tracking-wide text-neutral-500">Option 1</p>
            <label className="mt-2 block text-xs font-semibold text-neutral-800">Enter your bank statement password</label>
            <input
              data-testid="enc-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && password && !busy) onSubmitPassword(password); }}
              className="mt-1.5 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm outline-none focus:border-[#0A8B85]"
              placeholder="••••••••"
            />
            <p className="mt-1.5 text-[11px] leading-snug text-neutral-500">
              Your password is used only to unlock this document and is never stored.
            </p>
            <button
              data-testid="enc-password-submit"
              disabled={!password || busy}
              onClick={() => onSubmitPassword(password)}
              className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#0A8B85] px-3 py-2 text-xs font-bold text-white hover:bg-[#066667] disabled:opacity-50"
            >
              {busy ? (<><Loader2 className="h-3.5 w-3.5 animate-spin" /> Unlocking…</>) : "Unlock and upload"}
            </button>
          </div>

          {/* Option 2 — upload unlocked version */}
          <div className="rounded-xl border border-neutral-200 p-4">
            <p className="text-xs font-bold uppercase tracking-wide text-neutral-500">Option 2</p>
            <p className="mt-2 text-[11px] leading-snug text-neutral-600">
              Open your bank statement on your phone or computer, enter your
              password when prompted, then save or export it as a new PDF
              without a password. Upload that version here.
            </p>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,application/pdf"
              className="hidden"
              data-testid="enc-unlocked-file-input"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) onPickUnlocked(f);
              }}
            />
            <button
              data-testid="enc-upload-unlocked-btn"
              disabled={busy}
              onClick={() => fileRef.current?.click()}
              className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-[#0A8B85]/40 bg-white px-3 py-2 text-xs font-bold text-[#066667] hover:bg-[#0A8B85]/5 disabled:opacity-50"
            >
              <Upload className="h-3.5 w-3.5" /> Upload unlocked version
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
